import React, { useEffect, useRef, useState } from "react";

import {
  colorToHSVA,
  colorToHex,
  hsvaToColor,
  type HSVA,
} from "@excalidraw/common";

import { defaultGradient } from "@excalidraw/element";

import type { ElementBackgroundGradient } from "@excalidraw/element/types";

import { ColorFormatFields } from "./ColorFormatFields";
import { GradientBar } from "./GradientBar";

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/** 2D saturation (x) / value (y) area. */
const SaturationArea = ({
  hsva,
  onChange,
}: {
  hsva: HSVA;
  onChange: (hsva: HSVA) => void;
}) => {
  const areaRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const el = areaRef.current;
    if (!el) {
      return;
    }
    el.setPointerCapture(event.pointerId);

    const update = (clientX: number, clientY: number) => {
      const rect = el.getBoundingClientRect();
      const s = clamp01((clientX - rect.left) / rect.width) * 100;
      const v = (1 - clamp01((clientY - rect.top) / rect.height)) * 100;
      onChange({ ...hsva, s, v });
    };

    update(event.clientX, event.clientY);
    const move = (e: PointerEvent) => update(e.clientX, e.clientY);
    const up = () => el.removeEventListener("pointermove", move);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up, { once: true });
    el.addEventListener("pointercancel", up, { once: true });
  };

  return (
    <div
      ref={areaRef}
      className="spectrum__area"
      style={{
        background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsva.h}, 100%, 50%))`,
      }}
      onPointerDown={handlePointerDown}
    >
      <div
        className="spectrum__area-thumb"
        style={{
          // 14px thumb, kept fully inside the area at all four edges
          left: `calc(${hsva.s}% + ${(0.5 - hsva.s / 100) * 14}px)`,
          top: `calc(${100 - hsva.v}% + ${(hsva.v / 100 - 0.5) * 14}px)`,
          background: hsvaToColor({ ...hsva, a: 1 }),
        }}
      />
    </div>
  );
};

/** Native range input, invisible over a gradient track, with a custom thumb. */
const PickerSlider = ({
  ariaLabel,
  min,
  max,
  step,
  value,
  gradient,
  thumbFill,
  checkerboard,
  onChange,
}: {
  ariaLabel: string;
  min: number;
  max: number;
  step: number;
  value: number;
  gradient: string;
  thumbFill: string;
  checkerboard?: boolean;
  onChange: (value: number) => void;
}) => {
  const ratio = max === min ? 0 : clamp01((value - min) / (max - min));
  return (
    <div className={`spectrum__slider${checkerboard ? " checkerboard" : ""}`}>
      <div
        className="spectrum__slider-gradient"
        style={{ background: gradient }}
      />
      <input
        type="range"
        aria-label={ariaLabel}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        onKeyDown={(e) => e.stopPropagation()}
      />
      <div
        className="spectrum__slider-thumb"
        style={{
          // 14px thumb: keep it inside the track at both ends
          left: `calc(${ratio * 100}% - ${ratio * 14}px)`,
          background: thumbFill,
        }}
      />
    </div>
  );
};

export const Spectrum = ({
  color,
  onChange,
  gradient,
  onGradientChange,
  children,
}: {
  color: string | null;
  onChange: (color: string) => void;
  /** current background gradient (enables gradient tabs when combined with
      onGradientChange) */
  gradient?: ElementBackgroundGradient | null;
  onGradientChange?: (gradient: ElementBackgroundGradient | null) => void;
  /** hex input + eyedropper row, rendered below the picker */
  children?: React.ReactNode;
}) => {
  const [selectedStop, setSelectedStop] = useState(0);

  // in gradient mode the color editors operate on the selected stop
  const stopIndex = gradient
    ? Math.min(selectedStop, gradient.stops.length - 1)
    : 0;
  const activeColor = gradient
    ? gradient.stops[stopIndex]?.color ?? "#000000"
    : color || "#000000";

  const [hsva, setHsva] = useState<HSVA>(() => colorToHSVA(activeColor));

  // Re-sync from the active color only on a genuine external change, so live
  // dragging isn't clobbered and the hue isn't lost when value/saturation hit 0.
  useEffect(() => {
    if (colorToHex(activeColor) !== colorToHex(hsvaToColor(hsva))) {
      setHsva(colorToHSVA(activeColor));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeColor]);

  const setActiveColor = (c: string) => {
    if (gradient && onGradientChange) {
      onGradientChange({
        ...gradient,
        stops: gradient.stops.map((s, i) =>
          i === stopIndex ? { ...s, color: c } : s,
        ),
      });
    } else {
      onChange(c);
    }
  };

  const emit = (next: HSVA) => {
    setHsva(next);
    setActiveColor(hsvaToColor(next));
  };

  // picking a color (area / hue) on a fully transparent start makes it opaque,
  // otherwise the picked color would stay invisible
  const pick = (next: HSVA) => emit(next.a === 0 ? { ...next, a: 1 } : next);

  const currentMode: "solid" | "linear" | "radial" = gradient
    ? gradient.type
    : "solid";

  const selectMode = (mode: "solid" | "linear" | "radial") => {
    if (!onGradientChange) {
      return;
    }
    if (mode === "solid") {
      onGradientChange(null);
    } else if (!gradient) {
      setSelectedStop(0);
      onGradientChange(defaultGradient(mode, color || "#4dabf7"));
    } else if (gradient.type !== mode) {
      onGradientChange({ ...gradient, type: mode });
    }
  };

  return (
    <div className="spectrum">
      {onGradientChange && (
        <div className="spectrum__tabs">
          {(["solid", "linear", "radial"] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={`spectrum__tab${currentMode === m ? " selected" : ""}`}
              onClick={() => selectMode(m)}
            >
              {m === "solid" ? "Solid" : m === "linear" ? "Linear" : "Radial"}
            </button>
          ))}
        </div>
      )}

      {gradient && onGradientChange && (
        <>
          <GradientBar
            gradient={gradient}
            selectedStop={stopIndex}
            onSelectStop={setSelectedStop}
            onChange={onGradientChange}
          />
          {gradient.type === "linear" && (
            <label className="spectrum__angle">
              <span>Angle</span>
              <input
                type="number"
                min={0}
                max={360}
                value={Math.round(gradient.angle)}
                onChange={(e) => {
                  const a = parseFloat(e.target.value);
                  if (!Number.isNaN(a)) {
                    onGradientChange({
                      ...gradient,
                      angle: ((a % 360) + 360) % 360,
                    });
                  }
                }}
                onKeyDown={(e) => e.stopPropagation()}
              />
              <span className="spectrum__angle-unit">°</span>
            </label>
          )}
        </>
      )}

      <SaturationArea hsva={hsva} onChange={pick} />

      <PickerSlider
        ariaLabel="Hue"
        min={0}
        max={360}
        step={1}
        value={hsva.h}
        gradient="linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)"
        thumbFill={`hsl(${hsva.h}, 100%, 50%)`}
        onChange={(h) => pick({ ...hsva, h })}
      />

      <PickerSlider
        ariaLabel="Alpha"
        min={0}
        max={1}
        step={0.01}
        value={hsva.a}
        gradient={`linear-gradient(to right, transparent, ${hsvaToColor({
          ...hsva,
          a: 1,
        })})`}
        thumbFill={hsvaToColor({ ...hsva, a: 1 })}
        checkerboard
        onChange={(a) => emit({ ...hsva, a })}
      />

      <ColorFormatFields hsva={hsva} onChange={emit} />

      {children}
    </div>
  );
};
